import { Card, Text, Badge, Flex } from '@tremor/react'
import {
  CpuChipIcon,
  WifiIcon,
  LinkIcon,
  SpeakerWaveIcon,
  MicrophoneIcon,
  BoltIcon,
  Battery50Icon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import type { MachineWithMetrics, VoipState } from '@/types'
import { PhoneIndicator } from './VoipStatusBadge'
import { getMachineStatusColor, getMachineStatusLabel } from '@/utils/machineStatus'
import { getQualityVisual } from '@/utils/networkQuality'
import { getAgentVersionStatus } from '@/utils/agentVersion'
import { getEmcallStatus, emcallDriftLabel } from '@/utils/audioEmcall'

interface OperatorCardProps {
  machine: MachineWithMetrics
  voipState?: VoipState | null
  onClick?: () => void
}

export function OperatorCard({ machine, voipState, onClick }: OperatorCardProps) {
  const { metrics } = machine

  // Check if any audio device doesn't match expected
  const hasAudioDeviceMismatch = metrics && (
    metrics.input_device_match === false || metrics.output_device_match === false
  )

  // Composite VoIP quality colors the left border so the dispatcher
  // sees the connection level at a glance. Falls back to no border
  // when the agent hasn't shipped a quality block yet.
  const nq = metrics?.network_quality ?? null
  const qualityVisual = getQualityVisual(nq?.level)
  const qualityBorderStyle = nq?.level
    ? { borderLeft: `4px solid ${qualityVisual.cssBorder}` }
    : undefined

  const extension = machine.extension || machine.machine_id.replace('operator-', '')
  const agentStatus = getAgentVersionStatus(machine.agent_version)
  const emcall = getEmcallStatus(metrics)
  const emcallDrifted = emcall.known && !emcall.ok

  // A stale agent (red ring) takes visual priority over an audio device
  // mismatch (orange ring) — an out-of-date agent can't run recent
  // remote fixes, so it's the more urgent signal.
  const ringClass = agentStatus.stale || emcallDrifted
    ? 'ring-2 ring-red-500 dark:ring-red-500'
    : hasAudioDeviceMismatch
      ? 'ring-2 ring-orange-400 dark:ring-orange-500'
      : ''

  return (
    <Card
      className={`cursor-pointer hover:shadow-lg transition-shadow ${ringClass}`}
      style={qualityBorderStyle}
      onClick={onClick}
    >
      {/* Header */}
      <Flex justifyContent="between" alignItems="start" className="mb-4">
        <div>
          <Flex justifyContent="start" className="gap-2">
            <Text className="font-semibold text-gray-900 dark:text-gray-100">{machine.operator_name || machine.name}</Text>
            {/* VoIP Status Indicator */}
            {voipState && (
              <PhoneIndicator
                status={voipState.status}
                isInCall={voipState.is_in_call}
              />
            )}
            {hasAudioDeviceMismatch && (
              <ExclamationTriangleIcon className="w-4 h-4 text-orange-500" title="Mauvais peripherique audio" />
            )}
          </Flex>
          <Text className="text-xs text-gray-500">
            <span className="font-mono text-gray-400">#{extension}</span> · {machine.location}
          </Text>
        </div>
        <Flex justifyContent="end" className="gap-1 flex-wrap">
          {agentStatus.stale && (
            <Badge color="red" title={`Agent obsolète (${agentStatus.label}, ${agentStatus.ageDays} j) — mettez à jour`}>
              ⚠ Agent v. ancienne
            </Badge>
          )}
          {emcallDrifted && (
            <Badge
              color="red"
              title={`${emcallDriftLabel(emcall)} — micro: ${metrics?.input_device || '?'} / sortie: ${metrics?.output_device || '?'}`}
            >
              🎙 {emcallDriftLabel(emcall)}
            </Badge>
          )}
          {nq?.connection_type === 'mobile_hotspot' && (
            <Badge color="orange" title="Connexion via partage 4G d'un téléphone — qualité instable">
              📱 Hotspot
            </Badge>
          )}
          {nq?.level && (
            <Badge color={qualityVisual.tremor} title={nq.verbal_reason || ''}>
              {qualityVisual.dot} {nq.medium_label ? `${nq.medium_label} ` : ''}{nq.level_label}
            </Badge>
          )}
          {metrics?.activity_afk === true && (
            <Badge color="gray" title="Opérateur inactif (aucune activité détectée)">
              ⏸ Inactif
            </Badge>
          )}
          <Badge color={getMachineStatusColor(machine.status)}>
            {getMachineStatusLabel(machine.status)}
          </Badge>
        </Flex>
      </Flex>

      {metrics ? (
        <div className="space-y-3">
          {/* Row 1: Network (WiFi/Ethernet) - HIGH PRIORITY */}
          <Flex justifyContent="between" alignItems="center" className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
            <Flex justifyContent="start" className="gap-2">
              {metrics.network_type === 'ethernet' ? (
                <LinkIcon className="w-5 h-5 text-green-500" title="Connexion filaire Ethernet" />
              ) : (
                <WifiIcon className={`w-5 h-5 ${
                  metrics.wifi_signal === null ? 'text-gray-400' :
                  metrics.wifi_signal >= 70 ? 'text-green-500' :
                  metrics.wifi_signal >= 50 ? 'text-yellow-500' : 'text-red-500'
                }`} />
              )}
              <div>
                <Text className="text-sm font-medium">
                  {metrics.network_type === 'ethernet' ? '🔌 Câble' :
                   metrics.wifi_signal !== null ? `${metrics.wifi_signal}%` : '-'}
                </Text>
                {metrics.wifi_ssid && (
                  <Text className="text-xs text-gray-500 truncate max-w-[100px]">{metrics.wifi_ssid}</Text>
                )}
              </div>
            </Flex>
            <Flex justifyContent="end" className="gap-3">
              <div className="text-right">
                <Text className="text-xs text-gray-500">Ping</Text>
                <Text className={`text-sm font-medium ${
                  metrics.ping_ms === null ? 'text-gray-400' :
                  metrics.ping_ms < 50 ? 'text-green-600 dark:text-green-400' :
                  metrics.ping_ms < 100 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {metrics.ping_ms !== null ? `${Math.round(metrics.ping_ms)}ms` : '-'}
                </Text>
              </div>
              <div className="text-right">
                <Text className="text-xs text-gray-500">Jitter</Text>
                <Text className={`text-sm font-medium ${
                  metrics.jitter_ms === null ? 'text-gray-400' :
                  metrics.jitter_ms < 20 ? 'text-green-600 dark:text-green-400' :
                  metrics.jitter_ms < 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {metrics.jitter_ms !== null ? `${Math.round(metrics.jitter_ms)}ms` : '-'}
                </Text>
              </div>
            </Flex>
          </Flex>

          {/* Row 2: Audio & Power - HIGH PRIORITY */}
          <Flex justifyContent="between" alignItems="center">
            {/* Audio - Micro + Sortie */}
            <Flex justifyContent="start" className="gap-4">
              {/* Microphone */}
              <Flex justifyContent="start" className="gap-1" title={emcall.known && !emcall.micOk ? `Micro hors EMCALL: ${metrics.input_device}` : 'Microphone'}>
                <MicrophoneIcon
                  className={`w-5 h-5 ${
                    metrics.mic_muted ? 'text-red-500' :
                    emcall.known && !emcall.micOk ? 'text-red-500' :
                    metrics.mic_volume != null ? 'text-green-500' : 'text-gray-400'
                  }`}
                />
                <Text className={`text-sm font-medium ${metrics.mic_muted || (emcall.known && !emcall.micOk) ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {metrics.mic_muted ? 'Muet' : metrics.mic_volume != null ? `${metrics.mic_volume}%` : '--'}
                </Text>
              </Flex>
              {/* Speaker/Output */}
              <Flex justifyContent="start" className="gap-1" title={emcall.known && !emcall.outOk ? `Sortie hors EMCALL: ${metrics.output_device}` : 'Sortie audio'}>
                <SpeakerWaveIcon
                  className={`w-5 h-5 ${
                    metrics.output_muted ? 'text-red-500' :
                    emcall.known && !emcall.outOk ? 'text-red-500' :
                    metrics.output_volume != null ? 'text-blue-500' : 'text-gray-400'
                  }`}
                />
                <Text className={`text-sm font-medium ${metrics.output_muted || (emcall.known && !emcall.outOk) ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {metrics.output_muted ? 'Muet' : metrics.output_volume != null ? `${metrics.output_volume}%` : '--'}
                </Text>
              </Flex>
            </Flex>

            {/* Power Status */}
            <Flex justifyContent="end" className="gap-1">
              {metrics.has_battery === false ? (
                // Desktop PC - no battery
                <>
                  <ComputerDesktopIcon className="w-5 h-5 text-blue-500" />
                  <Text className="text-sm font-medium text-blue-600 dark:text-blue-400">Secteur</Text>
                </>
              ) : metrics.battery_plugged ? (
                // Laptop plugged in
                <>
                  <BoltIcon className="w-5 h-5 text-green-500" />
                  <Text className="text-sm font-medium text-green-600 dark:text-green-400">
                    {metrics.battery_percent !== null ? `${metrics.battery_percent}%` : 'Secteur'}
                  </Text>
                </>
              ) : (
                // Laptop on battery - warning style
                <>
                  <Battery50Icon className={`w-5 h-5 ${
                    metrics.battery_percent !== null && metrics.battery_percent < 20
                      ? 'text-red-500'
                      : 'text-yellow-500'
                  }`} />
                  <Text className={`text-sm font-medium ${
                    metrics.battery_percent !== null && metrics.battery_percent < 20
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-yellow-600 dark:text-yellow-400'
                  }`}>
                    {metrics.battery_percent !== null ? `${metrics.battery_percent}%` : 'Batterie'}
                  </Text>
                </>
              )}
            </Flex>
          </Flex>

          {/* Row 3: CPU/RAM - Lower priority, compact */}
          <Flex justifyContent="between" className="pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500">
            <Flex justifyContent="start" className="gap-1">
              <CpuChipIcon className="w-3.5 h-3.5" />
              <Text className="text-xs">CPU {metrics.cpu_percent}%</Text>
            </Flex>
            <Text className="text-xs">RAM {metrics.ram_percent}%</Text>
          </Flex>
        </div>
      ) : (
        <Text className="text-gray-400 text-sm">Aucune donnée</Text>
      )}
    </Card>
  )
}
